/*======================================================================================================================
  * Bluebell: src/app/state/sandbox/sandbox.effects.ts
  * Last Edited: 31/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import {
  createSandbox, createSandboxFailure, createSandboxSuccess,
  deleteSandbox, deleteSandboxFailure, deleteSandboxSuccess,
  listSandboxes, listSandboxesFailure, listSandboxesSuccess,
  updateSandbox, updateSandboxFailure, updateSandboxSuccess,
  selectSandbox,
} from '@state/sandbox/sandbox.actions';
import { Sandbox } from '@state/sandbox/sandbox.interfaces';
import { concatMap, mergeMap, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';


@Injectable()
export class SandboxEffects {
  actions$ = inject(Actions);
  api = inject(ApiService);
  handler = inject(EffectHandlerService);

  create$ = createEffect(() => this.actions$.pipe(
    ofType(createSandbox),
    mergeMap(({ partial }) => this.api.createSandbox(partial).pipe(
      this.handler.effectResponse({
        success: (sandbox: Sandbox) => createSandboxSuccess({ sandbox }),
        retry: createSandbox({ partial }),
        failure: createSandboxFailure(),
        failureMessage: 'Could not create sandbox'
      })
    ))
  ));

  createUpdateSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(createSandboxSuccess, updateSandboxSuccess),
    map(({ sandbox }) => selectSandbox({ selectedId: sandbox.uuid }))
  ));

  list$ = createEffect(() => this.actions$.pipe(
    ofType(listSandboxes),
    switchMap(() => this.api.listSandboxes().pipe(
      this.handler.effectResponse({
        success: (sandboxes: Sandbox[]) => listSandboxesSuccess({ sandboxes }),
        retry: listSandboxes(),
        failure: listSandboxesFailure(),
        failureMessage: 'Could not load sandboxes'
      })
    ))
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(updateSandbox),
    mergeMap(({ partial }) => this.api.updateSandbox(partial).pipe(
      this.handler.effectResponse({
        success: (sandbox: Sandbox) => updateSandboxSuccess({ sandbox }),
        retry: updateSandbox({ partial }),
        failure: updateSandboxFailure(),
        failureMessage: 'Could not update sandbox'
      })
    ))
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(deleteSandbox),
    concatMap(({ uuid }) => this.api.deleteSandbox(uuid).pipe(
      this.handler.effectResponse({
        success: () => deleteSandboxSuccess({ uuid }),
        retry: deleteSandbox({ uuid }),
        failure: deleteSandboxFailure(),
        failureMessage: 'Could not delete sandbox'
      })
    ))
  ));

}
